import { useEffect, useMemo, useRef, useState } from "react"
import { DeepPartial } from "react-hook-form"
import { useIsoLayoutEffect } from "shared/lib/react/useIsoLayoutEffect"

import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { Floor } from "shared/types/Floor"
import { FloorTypename } from "shared/types/Project"
import { FormProviderProps, FormRef } from "shared/types/ReactHookForm"

import { useAttachmentsQuery } from "entities/attachments/api/useAttachmentsQuery"
import { useProjectAttachmentUpload } from "entities/attachments/api/useProjectAttachmentUpload"
import { useProjectAttachmentsData } from "entities/projects/api/useProjectAttachmentsData"
import { useProjectOptions } from "entities/projects/model/useProjectOptions"
import { useProjectPositionOptions } from "entities/projects/model/useProjectPositionOptions"
import { useFloorRoomOptions } from "features/floor-management/model/useFloorRoomOptions"
import { FloorFormData } from "features/floor-management/types/FloorFormData"
import { FloorFormModel } from "features/floor-management/types/FloorFormModel"
import { FloorFormOptions } from "features/floor-management/types/FloorFormOptions"
import { FloorModel } from "features/floor-management/types/FloorModel"

interface UseFloorFormArgs {
  floor?: Partial<Floor>
  projectID: string
  parentID: string
  displayPosition?: number
  model?: FloorModel | null

  formRef?: FormRef<FloorFormData>
  formOptions?: FloorFormOptions

  onChange?: (formData: FloorFormData) => void
  onSubmit: (formData: FloorFormData) => Promise<ApiResponse<unknown>> | void
}

export function useFloorForm({
  floor,
  projectID,
  parentID,
  displayPosition,
  model,
  formRef: propsFormRef,
  formOptions,
  onSubmit,
  onChange
}: UseFloorFormArgs): FloorFormModel {
  const crContent =
    model?.clarificationRequest?.data.clarificationRequest?.data?.content ??
    null

  const defaultFormData = useMemo<DeepPartial<FloorFormData>>(
    () => ({
      floor,
      parentID,
      projectID,
      displayPosition: displayPosition ?? 0,
      roomIDs: null,
      clarificationRequest: {
        content: crContent
      }
    }),
    [floor, parentID, projectID, displayPosition, crContent]
  )

  const formRef = useRef<FormProviderProps<FloorFormData> | null>(null)
  const [formData, setFormData] =
    useState<DeepPartial<FloorFormData>>(defaultFormData)

  useEffect(() => {
    return formRef.current?.subscribe({
      callback: ({ values }) => {
        setFormData(values)
      },
      formState: { values: true }
    })
  })

  const mergedRef = useMergedRefs<FormProviderProps<FloorFormData>>(
    propsFormRef,
    formRef
  )

  const { positionOptions, isLoading: isLoadingPositionOptions } =
    useProjectPositionOptions({
      childID: floor?.id,
      projectID: formData.projectID || null,
      parentID: formData.parentID,
      childType: FloorTypename
    })

  const { roomOptions, isLoading: isLoadingRoomOptions } = useFloorRoomOptions({
    projectID
  })

  const { options: projectOptions, isLoading: isLoadingProjectOptions } =
    useProjectOptions()

  const effectiveProjectID = formData.projectID || projectID

  const { attachments, attachmentCategories } = useProjectAttachmentsData({
    projectID: effectiveProjectID,
    options: { skip: !effectiveProjectID }
  })

  const { createAttachment } = useProjectAttachmentUpload()

  const { data: linkedAttachmentsData } = useAttachmentsQuery({
    id: floor?.id ?? "",
    resourceType: FloorTypename,
    skip: !floor?.id
  })

  const handleUpload = useEvent((file: FilePreview, category: string) => {
    return createAttachment(file.file, {
      projectID: effectiveProjectID,
      category
    })
  })

  useIsoLayoutEffect(() => {
    formRef.current?.reset(defaultFormData, {
      keepDirty: true,
      keepDirtyValues: true
    })
  }, [defaultFormData])

  return {
    defaultFormData,
    formRef: mergedRef,
    formOptions,
    model,

    positionOptions,
    isLoadingPositionOptions,

    roomOptions,
    isLoadingRoomOptions,

    projectOptions,
    isLoadingProjectOptions,

    attachments: attachments?.data ?? null,
    linkedAttachments: linkedAttachmentsData?.attachments ?? null,
    attachmentCategories: attachmentCategories?.data ?? null,
    onUpload: handleUpload,

    onSubmit,
    onChange
  }
}
